import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';

import { getMemberObjects } from '@core/api/graph.api';
import { fetchServerAccess } from '@core/api/me.api';
import { buildUserData, useUserStore } from '@core/state/user.store';
import { useNotificationsStore, useUnreadCount } from '@core/state/notifications.store';
import { useThemeStore } from '@core/state/theme.store';
import { useActiveWorkspace, useWorkspaceStore, type WorkspaceRegion } from '@core/state/workspace.store';
import { Icon } from '@ui/Icon';
import { Menu, MenuItem } from '@ui/Menu';
import { AssignmentsSearchInput } from '@features/my-assignments/components/AssignmentsSearchInput';

import './ToolBar.scss';

/**
 * The top bar.
 *
 * A plain `<header class="tool-bkg">` - it is a styled flex row, and every
 * rule in ToolBar.scss addresses the class rather than the element.
 *
 * The two pop-up panels are `<Menu>` instances carrying `panelClass`, so
 * `.notification-menu-panel` and `.workspace-menu-panel` style them.
 *
 * Establishing the signed-in user happens here rather than in the shell
 * because this component owns the role picker, so it owns resolving the
 * roles.
 */

export interface ToolBarProps {
  onToggleSidenav: () => void;
  /** Omitted while the `assistant` flag is off, which hides the button. */
  onToggleRightSidenav?: () => void;
  onLoginRedirect: () => void;
  showSearch?: boolean;
  searchTerm?: string;
  searchSuggestions?: string[];
  onSearchChanged: (value: string) => void;
  /** Enter in the search. Off the assignment screens it carries the term there. */
  onSearchSubmit?: (value: string) => void;
  /** The AI Mode button beside the search: asks the term as a question. */
  onAiMode?: (value: string) => void;
}

export function ToolBar({
  onToggleSidenav,
  onToggleRightSidenav,
  showSearch = false,
  searchTerm = '',
  searchSuggestions = [],
  onSearchChanged,
  onSearchSubmit,
  onAiMode
}: ToolBarProps) {
  const resolvedTheme = useThemeStore((state) => state.resolved);
  const logoTone = resolvedTheme === 'dark' ? 'white' : 'indigo';
  const { instance } = useMsal();
  const setUserData = useUserStore((state) => state.setUserData);

  const notifications = useNotificationsStore((state) => state.notifications);
  const dismissNotification = useNotificationsStore((state) => state.dismiss);
  const clearNotifications = useNotificationsStore((state) => state.clearAll);
  const markAllRead = useNotificationsStore((state) => state.markAllRead);
  const unreadCount = useUnreadCount();

  const workspaces = useWorkspaceStore((state) => state.workspaces);
  const setWorkspace = useWorkspaceStore((state) => state.setWorkspace);
  const activeWorkspace = useActiveWorkspace();

  /*
   * The outage banner self-dismisses after ten seconds.
   *
   * This is a placeholder for a real service health check, and is meant to be
   * driven by an API response rather than started `true` on every load. Kept
   * as-is rather than quietly dropped or quietly wired to something real.
   */
  const [isOutage, setIsOutage] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsOutage(false), 10_000);
    return () => clearTimeout(timer);
  }, []);

  /*
   * Resolve the signed-in user and their roles.
   *
   * Group ids come from three places, merged and de-duplicated: the `groups`
   * and `roles` claims on the id token (tenants populate one or the other) and
   * Graph's `getMemberObjects`. Graph is queried even when the token already
   * carries groups, because the token's list is truncated once a user is in
   * enough of them.
   */
  useEffect(() => {
    let cancelled = false;

    async function resolveUser() {
      const account = instance.getActiveAccount();

      if (!account?.idToken || !account.idTokenClaims) {
        return;
      }

      const claims = account.idTokenClaims as Record<string, unknown>;
      const tokenGroups = Array.isArray(claims['groups']) ? (claims['groups'] as string[]) : [];
      const tokenRoles = Array.isArray(claims['roles']) ? (claims['roles'] as string[]) : [];
      const graphGroups = await getMemberObjects(account.localAccountId);

      if (cancelled) {
        return;
      }

      const distinct = new Set<string>();

      for (const value of [...tokenGroups, ...tokenRoles, ...graphGroups]) {
        const normalized = String(value ?? '').trim();

        if (normalized) {
          distinct.add(normalized);
        }
      }

      // Trim the trailing four characters: the tenant's display names carry a
      // suffix that is not part of the name.
      const displayName = account.name ? account.name.slice(0, -4) : 'Unknown User';

      /*
       * Region, for the region-gated modules.
       *
       * Entra sends the office country as `ctry`; tenants that model region
       * separately send `region`. Either is accepted, neither is required -
       * a user with no country claim simply belongs to no gated region.
       */
      const region =
        (claims['region'] as string | undefined) ??
        (claims['ctry'] as string | undefined) ??
        (claims['country'] as string | undefined) ??
        null;

      // Roles and region come from the claims only. This used to append
      // IVS.Valuer, IVS.Admin and region SG for every user, which made everyone
      // an administrator on screen.
      const userData = buildUserData(displayName, account.username || 'No Email', account.localAccountId, [...distinct], region);

      // Keep the server's answer from the cached user (see user-cache.ts) until
      // the fresh one arrives, so the nav does not drop back to the claims and
      // then forward again on every load.
      const previous = useUserStore.getState().userData;

      setUserData(previous?.userId === userData.userId && previous.access ? { ...userData, access: previous.access } : userData);

      // Then the server's resolved permissions, which decide once they arrive.
      const access = await fetchServerAccess();

      if (!cancelled && access) {
        setUserData({ ...userData, access });
      }
    }

    void resolveUser();

    return () => {
      cancelled = true;
    };
  }, [instance, setUserData]);

  return (
    <header className={['tool-bkg', isOutage ? 'is-outage' : null].filter(Boolean).join(' ')}>
      <div className="toolbar-left">
        <button
          className="menu-button cw-icon-button"
          type="button"
          aria-label="Toggle navigation"
          onClick={onToggleSidenav}
        >
          <Icon className="nav-icon">menu</Icon>
        </button>
        <div className="brand" role="img" aria-label="Cushman &amp; Wakefield - Valuation &amp; Advisory">
          <img className="brand__mark brand__mark--lead" src={`/assets/images/brand/cw-mark-${logoTone}.png`} alt="" width={291} height={215} />
          <span className="brand__product">Valuation &amp; Advisory</span>
        </div>
      </div>

      {showSearch && (
        <form
          className="toolbar-center"
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            onSearchSubmit?.(searchTerm);
          }}
        >
          <AssignmentsSearchInput
            searchTerm={searchTerm}
            suggestions={searchSuggestions}
            placeholder="Search assignments, opportunities, clients…"
            ariaLabel="Search assignments, opportunities and clients"
            onSearchChanged={onSearchChanged}
          />
          {onAiMode && (
            <button
              type="button"
              className="toolbar-aimode"
              title="Ask in AI Mode"
              onClick={() => onAiMode(searchTerm)}
            >
              <Icon>auto_awesome</Icon>
              <span className="toolbar-aimode__label">AI Mode</span>
            </button>
          )}
        </form>
      )}

      <div className="toolbar-right">
        <Menu
          xPosition="before"
          panelClass="notification-menu-panel"
          trigger={({ onClick }) => (
            <button
              className="notification-button cw-icon-button"
              type="button"
              // Disabled until notifications are backed by a real feed.
              disabled
              aria-label={unreadCount ? `${unreadCount} unread notifications` : 'Notifications'}
              onClick={(event) => {
                onClick(event);
                markAllRead();
              }}
            >
              <Icon className="nav-icon">notifications</Icon>
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </button>
          )}
        >
          {/* Stops a click anywhere in the tray from dismissing it - only the
              dismiss and clear buttons should close anything. */}
          <div className="notification-panel" onClick={(event) => event.stopPropagation()}>
            <div className="notification-panel__header">
              <span className="notification-panel__title">Notifications</span>
              {notifications.length > 0 && (
                <button type="button" className="notification-panel__clear" onClick={clearNotifications}>
                  Clear all
                </button>
              )}
            </div>

            {/* Five items fit the viewport; anything beyond that scrolls. */}
            {notifications.length > 0 ? (
              <div className="notification-panel__list">
                {notifications.map((notification) => (
                  <div className="notification-item" key={notification.id}>
                    <span
                      className={[
                        'notification-item__dot',
                        notification.read ? 'notification-item__dot--read' : null
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      aria-hidden="true"
                    />
                    <div className="notification-item__body">
                      <span className="notification-item__headline">{notification.headline}</span>
                      <span className="notification-item__detail">{notification.detail}</span>
                    </div>
                    <button
                      type="button"
                      className="notification-item__dismiss"
                      aria-label="Dismiss notification"
                      onClick={() => dismissNotification(notification.id)}
                    >
                      <Icon>close</Icon>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="notification-panel__empty">You&apos;re all caught up.</div>
            )}
          </div>
        </Menu>

        {onToggleRightSidenav && (
          <button
            className="assistant-button cw-icon-button"
            type="button"
            aria-label="Open universal assistant"
            onClick={onToggleRightSidenav}
          >
            <Icon className="nav-icon">auto_awesome</Icon>
          </button>
        )}

        <Menu
          xPosition="before"
          panelClass="home-menu-panel workspace-menu-panel"
          trigger={({ onClick }) => (
            <button className="workspace-button" type="button" aria-label="Switch workspace" onClick={onClick}>
                            <span className="workspace-button__value">{activeWorkspace.label}</span>
              <Icon className="workspace-button__chevron">expand_more</Icon>
            </button>
          )}
        >
          {workspaces.map((workspace) => (
            <MenuItem
              key={workspace.id}
              className={workspace.id === activeWorkspace.id ? 'active' : undefined}
              onClick={() => setWorkspace(workspace.id as WorkspaceRegion)}
            >
              <span className="workspace-menu-item">
                <span className="workspace-menu-item__label">{workspace.label}</span>
                <span className="workspace-menu-item__desc">{workspace.description}</span>
              </span>
              {workspace.id === activeWorkspace.id && <Icon>check</Icon>}
            </MenuItem>
          ))}
        </Menu>
      </div>
    </header>
  );
}

export default ToolBar;
